/**
 * QuizResult Component
 *
 * Displays the result of a completed quiz, including pass/fail status, score, and options
 * to view detailed answers or return to the menu. Handles both percentage and actual score types.
 *
 * @module components/subQuest/quizzes/QuizResult
 */

import PropTypes from 'prop-types';

// Chakra UI components
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { ArrowBackIcon, CheckCircleIcon, CloseIcon } from '@chakra-ui/icons';

/**
 * QuizResult
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isShowResult - Whether to show the result status and score
 * @param {boolean} props.isShowAnswer - Whether to show the "Display Results" button
 * @param {number} props.score - User's score
 * @param {number} props.totalMark - Total possible score
 * @param {number} props.passingScore - Passing score (percentage or actual)
 * @param {string} props.scoreType - Score type ('Actual Score' or percentage)
 * @param {Array} props.questionnaires - Array of questionnaire results
 * @param {Function} props.handleRestart - Callback to return to the menu
 * @param {Function} props.handleOpenQuestionnaireMd - Callback to open questionnaire modal
 * @returns {JSX.Element}
 */
function QuizResult({
  isShowResult,
  isShowAnswer,
  score,
  totalMark,
  passingScore,
  scoreType,
  questionnaires,
  handleRestart,
  handleOpenQuestionnaireMd,
}) {
  const buttonColor = useColorModeValue('red', 'teal');
  const borderColor = useColorModeValue('gray.200', 'teal');

  const percentScore =
    totalMark > 0
      ? `${parseFloat((score / totalMark) * 100).toFixed(2)}%`
      : `${parseFloat(score.toFixed(2))}`;

  const scoreDisplay =
    scoreType !== 'Actual Score' ? percentScore : parseFloat(score.toFixed(2));

  /**
   * Determines the result status: Passed, Failed, or Submitted.
   * @returns {string}
   */
  const getResultStatus = () => {
    let status = '';

    if (isShowResult) {
      if (
        (scoreType !== 'Actual Score' &&
          totalMark > 0 &&
          parseFloat((score / totalMark) * 100).toFixed(2) >= passingScore) ||
        (scoreType === 'Actual Score' && score >= passingScore)
      ) {
        status = 'Passed';
      } else {
        status = 'Failed';
      }
    } else {
      status = 'Submitted';
    }

    return status;
  };

  return (
    <Flex h="100%">
      <Center w="100%">
        <Box w={250} borderRadius="md" border="1px" borderColor={borderColor}>
          <Center>
            <Text px={4} pt={4} fontSize="md" fontWeight={500}>
              Quiz Result
            </Text>
          </Center>

          <Divider my={3} />

          <Box px={4} pb={4} fontSize="sm">
            {/* Pass/Fail Icon */}
            <Center>
              {!isShowResult || getResultStatus() === 'Passed' ? (
                <CheckCircleIcon boxSize="50px" color="green.500" />
              ) : (
                <Flex
                  flexDirection="column"
                  justify="center"
                  alignItems="center"
                  bg="red.500"
                  rounded="50px"
                  w="55px"
                  h="55px"
                  textAlign="center"
                >
                  <CloseIcon boxSize="20px" color="white" />
                </Flex>
              )}
            </Center>

            {/* Result Status */}
            <Center my={4}>
              <Text fontSize="2xl">{getResultStatus()}</Text>
            </Center>

            {/* Score and details */}
            {isShowResult && (
              <Box>
                <Box
                  bg={getResultStatus() === 'Passed' ? 'green.50' : 'red.50'}
                  border="2px solid"
                  borderColor={
                    getResultStatus() === 'Passed' ? 'green.200' : 'red.200'
                  }
                  borderRadius="lg"
                  p={3}
                  mb={3}
                  textAlign="center"
                >
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    Your Score
                  </Text>
                  <Text
                    fontSize="3xl"
                    fontWeight="bold"
                    color={
                      getResultStatus() === 'Passed' ? 'green.600' : 'red.600'
                    }
                  >
                    {scoreDisplay}
                  </Text>
                </Box>
                <Text fontSize="sm" mb={1}>
                  Passing Score:&nbsp;
                  <Text as="span" fontWeight={500}>
                    {passingScore}
                    {scoreType !== 'Actual Score' ? '%' : ''}
                  </Text>
                </Text>
                <Text fontSize="sm">
                  Total Mark:&nbsp;
                  <Text as="span" fontWeight={500}>
                    {totalMark}
                  </Text>
                </Text>
              </Box>
            )}

            {/* Display Results button */}
            {isShowAnswer && questionnaires && (
              <Button
                mt={4}
                w="100%"
                colorScheme={buttonColor}
                onClick={handleOpenQuestionnaireMd}
              >
                Display Results
              </Button>
            )}

            {/* Return to menu button */}
            <Button
              mt={2}
              w="100%"
              variant="outline"
              colorScheme={buttonColor}
              onClick={handleRestart}
              leftIcon={<ArrowBackIcon />}
            >
              Menu
            </Button>
          </Box>
        </Box>
      </Center>
    </Flex>
  );
}

QuizResult.propTypes = {
  isShowResult: PropTypes.bool.isRequired,
  isShowAnswer: PropTypes.bool.isRequired,
  score: PropTypes.number.isRequired,
  totalMark: PropTypes.number.isRequired,
  passingScore: PropTypes.number.isRequired,
  scoreType: PropTypes.string.isRequired,
  questionnaires: PropTypes.array.isRequired,
  handleRestart: PropTypes.func.isRequired,
  handleOpenQuestionnaireMd: PropTypes.func.isRequired,
};

export default QuizResult;
